import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuthState } from 'react-firebase-hooks/auth';
import { auth } from '../firebase';
import { theme, responsiveContainer } from '../styles/theme';
import ByteBotLogo from './ByteBotLogo';

function Navigation({ onGetStarted }) {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [user] = useAuthState(auth);
  const location = useLocation();

  const navItems = [
    { label: 'Home', path: '/' },
    { label: 'Features', path: '/features' },
    { label: 'About', path: '/about' },
    { label: 'Pricing', path: '/pricing' },
    // { label: 'Blog', path: '/blog' }, // Hidden for MVP
    { label: 'Contact', path: '/contact' },
  ];

  const styles = {
    nav: {
      backgroundColor: theme.colors.surface,
      borderBottom: `2px solid ${theme.colors.borderDark}`,
      padding: 0,
      position: 'sticky',
      top: 0,
      zIndex: 1000,
      boxShadow: theme.shadows.md,
    },
    navContent: {
      ...responsiveContainer,
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      height: '70px',
      padding: `0 ${theme.spacing.lg}`,
    },
    logo: {
      display: 'flex',
      alignItems: 'center',
      fontSize: theme.typography.fontSizes['2xl'],
      fontWeight: theme.typography.fontWeights.bold,
      color: theme.colors.text,
      textTransform: 'uppercase',
      letterSpacing: '0.1em',
      textDecoration: 'none',
      cursor: 'pointer',
      transition: 'color 0.2s ease',
      fontFamily: "'Courier New', 'Monaco', 'Menlo', monospace",
    },
    logoIcon: {
      marginRight: theme.spacing.sm,
      display: 'flex',
      alignItems: 'center',
    },
    logoAccent: {
      color: theme.colors.primary,
    },
    navLinks: {
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing.lg,
    },
    navLink: {
      color: theme.colors.text,
      fontSize: theme.typography.fontSizes.base,
      fontWeight: theme.typography.fontWeights.medium,
      textTransform: 'uppercase',
      letterSpacing: '0.05em',
      textDecoration: 'none',
      padding: `${theme.spacing.sm} ${theme.spacing.md}`,
      borderRadius: theme.borderRadius.sm,
      transition: 'all 0.2s ease',
      borderBottom: '3px solid transparent',
      fontFamily: "'Courier New', 'Monaco', 'Menlo', monospace",
    },
    activeNavLink: {
      color: theme.colors.primary,
      borderBottom: `3px solid ${theme.colors.primary}`,
      backgroundColor: `${theme.colors.primary}10`,
    },
    ctaButton: {
      backgroundColor: theme.colors.primary,
      color: '#fff',
      border: `2px solid ${theme.colors.primary}`,
      borderRadius: theme.borderRadius.sm,
      padding: '10px 20px',
      fontSize: theme.typography.fontSizes.sm,
      fontWeight: theme.typography.fontWeights.bold,
      textTransform: 'uppercase',
      letterSpacing: '0.05em',
      cursor: 'pointer',
      transition: 'all 0.2s ease',
      boxShadow: 'none',
      fontFamily: "'Courier New', 'Monaco', 'Menlo', monospace",
      textDecoration: 'none',
      display: 'inline-block',
    },
    dashboardButton: {
      backgroundColor: theme.colors.secondary,
      color: '#fff',
      border: `2px solid ${theme.colors.secondary}`,
      borderRadius: theme.borderRadius.sm,
      padding: '10px 20px',
      fontSize: theme.typography.fontSizes.sm,
      fontWeight: theme.typography.fontWeights.bold,
      textTransform: 'uppercase',
      letterSpacing: '0.05em',
      cursor: 'pointer',
      transition: 'all 0.2s ease',
      boxShadow: 'none',
      fontFamily: "'Courier New', 'Monaco', 'Menlo', monospace",
      textDecoration: 'none',
      display: 'inline-block',
    },
    mobileMenuButton: {
      display: 'none',
      backgroundColor: 'transparent',
      border: `2px solid ${theme.colors.borderDark}`,
      borderRadius: theme.borderRadius.md,
      padding: theme.spacing.sm,
      cursor: 'pointer',
      fontSize: theme.typography.fontSizes.lg,
      color: theme.colors.text,
      fontFamily: "'Courier New', 'Monaco', 'Menlo', monospace",
    },
    mobileMenu: {
      display: 'none',
      position: 'absolute',
      top: '100%',
      left: 0,
      right: 0,
      backgroundColor: theme.colors.surface,
      borderBottom: `2px solid ${theme.colors.borderDark}`,
      flexDirection: 'column',
      padding: theme.spacing.lg,
      gap: theme.spacing.md,
      boxShadow: theme.shadows.lg,
    },
    mobileMenuOpen: {
      display: 'flex',
    },
    mobileNavLink: {
      color: theme.colors.text,
      fontSize: theme.typography.fontSizes.lg,
      fontWeight: theme.typography.fontWeights.medium,
      textTransform: 'uppercase',
      letterSpacing: '0.05em',
      textDecoration: 'none',
      padding: theme.spacing.lg,
      textAlign: 'center',
      border: `2px solid ${theme.colors.border}`,
      borderRadius: theme.borderRadius.md,
      transition: 'all 0.2s ease',
      fontFamily: "'Courier New', 'Monaco', 'Menlo', monospace",
    },
    activeMobileNavLink: {
      backgroundColor: theme.colors.primary,
      color: '#fff',
      borderColor: theme.colors.primary,
    },
  };

  // Responsive breakpoint
  const isMobile = window.innerWidth <= 768;
  
  if (isMobile) {
    styles.navLinks.display = 'none';
    styles.mobileMenuButton.display = 'block';
  }

  const isActivePath = (path) => {
    if (path === '/') {
      return location.pathname === '/';
    }
    return location.pathname.startsWith(path);
  };

  // Dynamic button text and action
  const getCtaButton = () => {
    if (user) {
      return (
        <Link
          to="/dashboard"
          style={styles.dashboardButton}
          onMouseEnter={(e) => {
            e.target.style.backgroundColor = '#1a1a1a';
            e.target.style.transform = 'translateY(-1px)';
            e.target.style.boxShadow = theme.shadows.md;
          }}
          onMouseLeave={(e) => {
            e.target.style.backgroundColor = theme.colors.secondary;
            e.target.style.transform = 'translateY(0)';
            e.target.style.boxShadow = theme.shadows.sm;
          }}
        >
          [DASHBOARD]
        </Link>
      );
    } else {
      return (
        <button
          style={styles.ctaButton}
          onMouseEnter={(e) => {
            e.target.style.backgroundColor = theme.colors.primaryDark;
            e.target.style.transform = 'translateY(-1px)';
            e.target.style.boxShadow = theme.shadows.md;
          }}
          onMouseLeave={(e) => {
            e.target.style.backgroundColor = theme.colors.primary;
            e.target.style.transform = 'translateY(0)';
            e.target.style.boxShadow = theme.shadows.sm;
          }}
          onClick={onGetStarted}
        >
          [GET STARTED FREE]
        </button>
      );
    }
  };

  return (
    <nav style={styles.nav}>
      <div style={styles.navContent}>
        {/* Logo */}
        <Link to="/" style={styles.logo}>
          <div style={styles.logoIcon}>
            <ByteBotLogo 
              size={32} 
              color={theme.colors.secondary}
            />
          </div>
          [BYTE<span style={styles.logoAccent}>BOT</span>]
        </Link>

        {/* Desktop Navigation */}
        <div style={styles.navLinks}>
          {navItems.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              style={{
                ...styles.navLink,
                ...(isActivePath(item.path) ? styles.activeNavLink : {})
              }}
              onMouseEnter={(e) => {
                if (!isActivePath(item.path)) {
                  e.target.style.color = theme.colors.primary;
                  e.target.style.backgroundColor = `${theme.colors.primary}05`;
                }
              }}
              onMouseLeave={(e) => {
                if (!isActivePath(item.path)) {
                  e.target.style.color = theme.colors.text;
                  e.target.style.backgroundColor = 'transparent';
                }
              }}
            >
              {item.label}
            </Link>
          ))}
          
          {getCtaButton()}
        </div>

        {/* Mobile Menu Button */}
        <button
          style={styles.mobileMenuButton}
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
        >
          ≡
        </button>
      </div>

      {/* Mobile Menu */}
      <div style={{
        ...styles.mobileMenu,
        ...(isMobileMenuOpen ? styles.mobileMenuOpen : {})
      }}>
        {navItems.map((item) => (
          <Link
            key={item.path}
            to={item.path}
            style={{
              ...styles.mobileNavLink,
              ...(isActivePath(item.path) ? styles.activeMobileNavLink : {})
            }}
            onClick={() => setIsMobileMenuOpen(false)}
          >
            {item.label}
          </Link>
        ))}
        
        <div style={{marginTop: theme.spacing.md}}>
          {getCtaButton()}
        </div>
      </div>
    </nav>
  );
}

export default Navigation; 